/**
 * Patch 35.3b — Sticky Batch Action Bar.
 *
 * Appears at the bottom of the Content Review screen whenever the
 * founder has selected one or more approved image prompts. Reads
 * selection from ``BatchSelectionContext`` and opens the
 * ``BatchPreflightModal`` on submit — actual execution runs from
 * inside that modal so preflight is never skipped.
 */
import { useState } from "react";
import { Loader2, Sparkles, X } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { useBatchSelection } from "@/entities/ai-studio/BatchSelectionContext";
import { BatchPreflightModal } from "@/entities/ai-studio/BatchPreflightModal";

interface Props {
  workspaceId: string;
  packId: string | null;
  isAdmin: boolean;
}

export function BatchActionBar({ workspaceId, packId, isAdmin }: Props): JSX.Element | null {
  const { count, max, clear, selectedIds } = useBatchSelection();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!packId || count === 0) return null;

  const atMax = count >= max;

  return (
    <>
      <div
        data-testid="batch-action-bar"
        className="pointer-events-none fixed inset-x-0 bottom-4 z-40 flex justify-center px-4"
      >
        <div
          className="pointer-events-auto flex w-full max-w-2xl flex-wrap items-center gap-3 rounded-full border border-border/60 bg-background/95 px-4 py-2 shadow-lg backdrop-blur"
        >
          <div className="flex-1 text-sm">
            <span
              data-testid="batch-selected-count"
              className="font-semibold"
            >
              {atMax ? `${count} of ${max}` : count}
            </span>{" "}
            <span className="text-muted-foreground">
              image{count === 1 ? "" : "s"} selected
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            data-testid="batch-clear"
            onClick={clear}
            disabled={busy}
          >
            <X className="mr-1 h-4 w-4" />
            Clear
          </Button>
          <Button
            size="sm"
            data-testid="batch-render-selected"
            onClick={() => setOpen(true)}
            disabled={busy}
          >
            {busy ? (
              <>
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                Working…
              </>
            ) : (
              <>
                <Sparkles className="mr-1 h-4 w-4" />
                Render Selected
              </>
            )}
          </Button>
        </div>
      </div>
      {open && packId && (
        <BatchPreflightModal
          workspaceId={workspaceId}
          packId={packId}
          assetIds={selectedIds}
          isAdmin={isAdmin}
          onOpenChange={setOpen}
          onWorkingChange={setBusy}
        />
      )}
    </>
  );
}
